import Button from '@atoms/Button/Button';
import CloseButton from '@atoms/CloseButton/CloseButton';
import Icon from '@atoms/Icon/Icon';
import Modal from '@atoms/Modal/Modal';
import { IconName } from '@models/IIcon';
import { ILink } from '@models/ILink';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { Dispatch, SetStateAction } from 'react';

interface Props {
  readonly buttonText?: string;
  readonly description?: string;
  readonly isModalOpen: boolean;
  readonly ctaLink?: ILink;
  readonly secondaryTitle?: string;
  readonly title?: string;
  readonly type: InfoTypeEnum;
  readonly toggleModal: Dispatch<SetStateAction<boolean>>;
  readonly handleCTAClick?: () => void;
  readonly dataTestId?: string;
  readonly shouldLockBody?: boolean;
}

const InfoModal = ({
  buttonText,
  description,
  isModalOpen,
  ctaLink,
  secondaryTitle,
  title,
  toggleModal,
  type,
  handleCTAClick,
  dataTestId = 'CT-Info-Modal',
  shouldLockBody,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const hasCTAButton = !!handleCTAClick && !!buttonText;
  const hasCTALink = !!ctaLink?.url && !!ctaLink.name;

  const iconType: Record<
    Exclude<InfoTypeEnum, InfoTypeEnum.ContentOverlay>,
    IconName
  > = {
    [InfoTypeEnum.Error]: 'warning',
    [InfoTypeEnum.Information]: 'info',
    [InfoTypeEnum.Success]: 'check',
  };

  const iconColour = {
    [InfoTypeEnum.Error]: 'text-yellow',
    [InfoTypeEnum.Information]: 'text-green',
    [InfoTypeEnum.Success]: 'bg-yellow rounded-full p-2',
  };

  return (
    <Modal
      isOpen={isModalOpen}
      setIsOpen={toggleModal}
      modalClasses="flex flex-col justify-center items-center w-full h-full fixed top-0 bottom-0 left-0 right-0 bg-black-100/25 max-w-none"
      contentClasses="w-full p-0 flex justify-center"
      transparent
      showCloseButton={false}
      shouldLockBody={shouldLockBody}
    >
      <div
        className={classNames(
          'flex max-h-[90vh] w-full flex-col overflow-hidden rounded-lg bg-white p-3 sm:w-2/4 lg:p-7.5',
          type === InfoTypeEnum.ContentOverlay ? 'xl:w-[673px]' : 'xl:w-1/3'
        )}
        data-testid={`${dataTestId}-Info-Modal`}
      >
        <div className="flex justify-end">
          <CloseButton
            onClick={() => toggleModal(false)}
            className="relative cursor-pointer"
            size={isMobile ? '1.25rem' : '1.5rem'}
            data-testid={`${dataTestId}-Info-Modal-Close-Button`}
          />
        </div>

        <div
          className={classNames(
            type === InfoTypeEnum.ContentOverlay
              ? 'px-2 pt-2 pb-4 lg:px-6 lg:pb-7.5'
              : 'px-2.5 pt-5 pb-13 lg:px-5 lg:pt-7.5',
            { 'text-center': type === InfoTypeEnum.Success },
            'flex flex-col overflow-auto text-black-100'
          )}
        >
          {type !== InfoTypeEnum.ContentOverlay && (
            <Icon
              name={iconType[type]}
              size="3.375rem"
              colorClassName={iconColour[type]}
              className={classNames('mb-5 shrink-0 lg:mb-7.5', {
                'mx-auto': type === InfoTypeEnum.Success,
              })}
              data-testid={`${dataTestId}-Info-Modal-Icon`}
            />
          )}

          {(title || description) && (
            <div className="flex flex-col gap-y-2.5 lg:gap-y-2">
              {title && (
                <h4 data-testid={`${dataTestId}-Pop-Up-Title`}>
                  {title}
                  {secondaryTitle && (
                    <span className="text-grey-450"> {secondaryTitle}</span>
                  )}
                </h4>
              )}

              {description && (
                <div className="max-h-[30vh] overflow-y-auto">
                  <p
                    className="body-one whitespace-pre-line"
                    data-testid={`${dataTestId}-Pop-Up-Text`}
                  >
                    {description}
                  </p>
                </div>
              )}
            </div>
          )}

          {(hasCTAButton || hasCTALink) && (
            <div className="mt-5 lg:mt-10">
              {hasCTALink ? (
                <Button
                  link={ctaLink}
                  dataTestId={`${dataTestId}-Info-Modal-CTA-Link`}
                />
              ) : (
                <Button
                  onClick={handleCTAClick}
                  dataTestId={`${dataTestId}-Info-Modal-CTA`}
                >
                  {buttonText}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InfoModal;
